import { type JSX } from 'react';
import { useSelector } from 'react-redux';
import { Link, generatePath, useLocation } from 'react-router';
import { paths } from '../../common/constants/pathVariables.js';
import { billingAccountAndUserMsg, settingsMsg, t, technicalDataMsg } from '../../common/i18n';
import { AccordionsMain } from '../../common/utils/accordionUtils.js';
import { AuthenticatedUserRole } from '../../generated/api/authenticatedUserRole';
import { SubscriptionType } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { ChangeContractOwnerView } from './Accordions/ChangeContractOwnerAccordion.js';
import { getMobilePbxAccordion, getMobilePbxSettingsAccordion } from './Accordions/MobilePbxAccordions.js';
import type { Accordion } from '../../common/utils/accordionUtils.js';
import type {
  CompanyInfoResponse,
  Subscription,
  SubscriptionDetails as SubscriptionDetailsModel,
} from '../../generated/api/models.js';
import type { State } from '../../selfservice/common/store.js';

import './SubscriptionDetails.scss';

export interface SubscriptionDetailsProps {
  breadCrumbs?: JSX.Element;
  companyInfo: CompanyInfoResponse;
  hasPendingActions: boolean;
  subscription: Subscription;
}

interface AccordionsProps {
  subscription: Subscription;
  details: SubscriptionDetailsModel;
  companyInfo?: CompanyInfoResponse;
}

const Accordions = ({ subscription, details, companyInfo }: AccordionsProps) => {
  const { authenticatedUser } = useAuth();

  const changeContractOwnerAccordion: Accordion = {
    headerName: t.CZ63(billingAccountAndUserMsg),
    id: 'change-contract-owner',
    includedData: <ChangeContractOwnerView subscription={subscription} companyInfo={companyInfo} />,
    displayed:
      authenticatedUser?.userRole === AuthenticatedUserRole.ADMIN ||
      authenticatedUser?.userRole === AuthenticatedUserRole.KEY_USER,
  };

  switch (subscription.subscriptionType) {
    case SubscriptionType.MOBILE_PBX_LITE:
      return (
        <AccordionsMain
          accordions={[
            getMobilePbxAccordion(t.L2MX(technicalDataMsg), subscription, details.mobilePbx),
            changeContractOwnerAccordion,
          ]}
        />
      );

    case SubscriptionType.MOBILE_PBX:
      return (
        <AccordionsMain
          accordions={[
            getMobilePbxSettingsAccordion(t.EJQF(settingsMsg), details.mobilePbx),
            getMobilePbxAccordion(t.LJQ3('Numbering ranges'), subscription, details.mobilePbx),
            changeContractOwnerAccordion,
          ]}
        />
      );

    default:
      throw new Error('Invalid subscriptionType');
  }
};

// Unfortunately there's nothing separating netti lite subscriptions from regular ones in the data model and thus this is needed.
// TODO: if netti lite subscriptions acquire fields that separate them from regular mobilebroandband-type connections, this should be refactored.
export const isNettiLiteSubscription = (subscriptionName?: string) =>
  /(netti\s*lite\s*[45]\s*g)/.test(subscriptionName?.toLowerCase() || '');

export const SubscriptionDetailsAccordions = ({ companyInfo, subscription }: SubscriptionDetailsProps) => {
  const { suspendSubscriptionInitiated, terminateSubscriptionInitiated } = useSelector((s: State) => ({
    suspendSubscriptionInitiated: s?.selfservice?.pendingSubscriptionActions?.suspendSubscriptionInitiated || false,
    terminateSubscriptionInitiated: s?.selfservice?.pendingSubscriptionActions?.terminateSubscriptionInitiated || false,
  }));

  const showSuspendSubscriptionOptions = suspendSubscriptionInitiated || false;
  if (!subscription?.details || showSuspendSubscriptionOptions || terminateSubscriptionInitiated) {
    return null;
  }

  return <Accordions subscription={subscription} details={subscription.details} companyInfo={companyInfo} />;
};

export const SubscriptionDetailsBillingAccount = ({ subscription }: { subscription: Subscription }) => {
  const location = useLocation();
  const { search } = location;

  if (!(subscription?.billingAccountDisplayId && subscription.billingAccountId)) {
    return <span>—</span>;
  }

  return (
    <span>
      {subscription.billingAccountDisplayId && (
        <Link
          className="of-subscription-details__to-billing-account"
          to={
            generatePath(paths.BILLING_ACCOUNT, {
              billingAccountId: subscription.billingAccountDisplayId,
            }) + search
          }
        >
          {subscription.billingAccountDisplayId}
        </Link>
      )}
    </span>
  );
};
